// src/admin/AdminMessages.jsx

import React, { useState, useEffect } from 'react';
import { supabase } from 'src/lib/supabase';

const AdminMessages = () => {
  const [conversations, setConversations] = useState([]);
  const [filteredConversations, setFilteredConversations] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [showFlaggedOnly, setShowFlaggedOnly] = useState(false);
  const [selectedConversation, setSelectedConversation] = useState(null);
  const [messages, setMessages] = useState([]);
  const [showMessages, setShowMessages] = useState(false);

  useEffect(() => {
    loadConversations();
  }, []);

  useEffect(() => {
    filterConversations();
  }, [searchTerm, showFlaggedOnly, conversations]);

  const loadConversations = async () => {
    setLoading(true);

    const { data: convos, error } = await supabase
      .from('conversations')
      .select(`
        *,
        job_requests (
          id,
          description
        ),
        customer:customer_id ( full_name, phone ),
        professional:professional_id ( full_name, phone )
      `)
      .order('updated_at', { ascending: false });

    if (error) {
      console.error('Error loading conversations:', error);
      setLoading(false);
      return;
    }

    const convosWithDetails = await Promise.all((convos || []).map(async (conv) => {
      const { data: lastMessage } = await supabase
        .from('chat_messages')
        .select('*')
        .eq('conversation_id', conv.id)
        .order('created_at', { ascending: false })
        .limit(1);

      const { data: recentMessages } = await supabase
        .from('chat_messages')
        .select('content')
        .eq('conversation_id', conv.id)
        .eq('type', 'text')
        .order('created_at', { ascending: false })
        .limit(10);

      const flaggedKeywords = ['mpesa', 'whatsapp', 'outside', 'bypass', 'direct payment', 'cash', 'pay outside'];
      const hasFlaggedContent = recentMessages?.some(msg =>
        flaggedKeywords.some(keyword => msg.content?.toLowerCase().includes(keyword))
      ) || false;

      const participantNames = [conv.customer?.full_name, conv.professional?.full_name]
        .filter(Boolean).join(', ') || 'Unknown';

      return {
        ...conv,
        participants: participantNames,
        participantsList: [
          conv.customer ? { profiles: { full_name: conv.customer.full_name, role: 'client' } } : null,
          conv.professional ? { profiles: { full_name: conv.professional.full_name, role: 'pro' } } : null,
        ].filter(Boolean),
        lastMessage: lastMessage?.[0] || null,
        messageCount: recentMessages?.length || 0,
        is_flagged: conv.is_flagged || hasFlaggedContent,
        job: conv.job_requests,
      };
    }));

    setConversations(convosWithDetails);
    setLoading(false);
  };

  const filterConversations = () => {
    let filtered = [...conversations];
    
    if (searchTerm) {
      const term = searchTerm.toLowerCase();
      filtered = filtered.filter(conv => 
        conv.participants?.toLowerCase().includes(term) ||
        conv.job?.customer?.full_name?.toLowerCase().includes(term) ||
        conv.job?.customer?.phone?.includes(term) ||
        conv.job?.pro?.full_name?.toLowerCase().includes(term) ||
        conv.job?.pro?.phone?.includes(term) ||
        conv.lastMessage?.content?.toLowerCase().includes(term)
      );
    }
    
    if (showFlaggedOnly) {
      filtered = filtered.filter(conv => conv.is_flagged);
    }
    
    setFilteredConversations(filtered);
  };

  const toggleFlag = async (conversationId, currentFlag) => {
    const { error } = await supabase
      .from('conversations')
      .update({ is_flagged: !currentFlag })
      .eq('id', conversationId);
    
    if (error) {
      console.error('Error updating flag:', error);
      alert('Failed to update flag');
    } else {
      setConversations(prev => prev.map(conv =>
        conv.id === conversationId ? { ...conv, is_flagged: !currentFlag } : conv
      ));
    }
  };

  const viewMessages = async (conversation) => {
    setSelectedConversation(conversation);
    setShowMessages(true);
    
    const { data: msgs, error } = await supabase
      .from('chat_messages')
      .select(`
        *,
        sender:sender_id (
          full_name,
          role
        )
      `)
      .eq('conversation_id', conversation.id)
      .order('created_at', { ascending: true });
    
    if (error) {
      console.error('Error loading messages:', error);
    } else {
      setMessages(msgs || []);
    }
  };

  return (
    <div style={{ maxWidth: 1400, margin: '0 auto', padding: 24, fontFamily: 'system-ui, -apple-system, sans-serif' }}>
      <div style={{ marginBottom: 24 }}>
        <h1 style={{ fontSize: 28, fontWeight: 600, marginBottom: 8 }}>Message Monitoring</h1>
        <p style={{ color: '#6B7280' }}>View and flag conversations across the platform</p>
      </div>
      
      {/* Filters */}
      <div style={{ display: 'flex', gap: 16, marginBottom: 24, flexWrap: 'wrap', alignItems: 'center' }}>
        <input
          type="text"
          placeholder="Search by name, phone, or message..."
          value={searchTerm}
          onChange={(e) => setSearchTerm(e.target.value)}
          style={{
            flex: 1,
            minWidth: 250,
            padding: '10px 16px',
            border: '1px solid #E5E7EB',
            borderRadius: 8,
            fontSize: 14,
            outline: 'none'
          }}
        />
        
        <label style={{ display: 'flex', alignItems: 'center', gap: 8, cursor: 'pointer' }}>
          <input
            type="checkbox"
            checked={showFlaggedOnly}
            onChange={(e) => setShowFlaggedOnly(e.target.checked)}
          />
          <span>Show flagged only</span>
        </label>
        
        <button
          onClick={loadConversations}
          style={{
            padding: '8px 16px',
            backgroundColor: '#F3F4F6',
            border: '1px solid #E5E7EB',
            borderRadius: 8,
            cursor: 'pointer'
          }}
        >
          Refresh
        </button>
      </div>
      
      {!showMessages ? (
        /* Conversations Table */
        <div style={{
          backgroundColor: 'white',
          borderRadius: 12,
          border: '1px solid #E5E7EB',
          overflowX: 'auto'
        }}>
          <table style={{ width: '100%', borderCollapse: 'collapse' }}>
            <thead style={{ backgroundColor: '#F9FAFB', borderBottom: '1px solid #E5E7EB' }}>
              <tr>
                <th style={{ padding: 12, textAlign: 'left', fontSize: 14, fontWeight: 600 }}>Participants</th>
                <th style={{ padding: 12, textAlign: 'left', fontSize: 14, fontWeight: 600 }}>Job</th>
                <th style={{ padding: 12, textAlign: 'left', fontSize: 14, fontWeight: 600 }}>Last Message</th>
                <th style={{ padding: 12, textAlign: 'left', fontSize: 14, fontWeight: 600 }}>Messages</th>
                <th style={{ padding: 12, textAlign: 'left', fontSize: 14, fontWeight: 600 }}>Updated</th>
                <th style={{ padding: 12, textAlign: 'center', fontSize: 14, fontWeight: 600 }}>Flagged</th>
                <th style={{ padding: 12, textAlign: 'center', fontSize: 14, fontWeight: 600 }}>Actions</th>
              </tr>
            </thead>
            <tbody>
              {loading ? (
                <tr>
                  <td colSpan="7" style={{ padding: 40, textAlign: 'center', color: '#9CA3AF' }}>
                    Loading conversations...
                  </td>
                </tr>
              ) : filteredConversations.length === 0 ? (
                <tr>
                  <td colSpan="7" style={{ padding: 40, textAlign: 'center', color: '#9CA3AF' }}>
                    No conversations found
                  </td>
                </tr>
              ) : (
                filteredConversations.map(conv => (
                  <tr key={conv.id} style={{ borderBottom: '1px solid #F3F4F6' }}>
                    <td style={{ padding: 12 }}>
                      <div style={{ fontWeight: 500 }}>{conv.participants}</div>
                      <div style={{ fontSize: 12, color: '#6B7280' }}>
                        {conv.participantsList?.map(p => p.profiles?.role).join(', ')}
                      </div>
                    </td>
                    <td style={{ padding: 12 }}>
                      {conv.job?.description || 'N/A'}
                    </td>
                    <td style={{ padding: 12, maxWidth: 300 }}>
                      <div style={{
                        overflow: 'hidden',
                        textOverflow: 'ellipsis',
                        whiteSpace: 'nowrap',
                        fontSize: 13,
                        color: '#374151'
                      }}>
                        {conv.lastMessage?.type === 'image' ? '📷 Image' : conv.lastMessage?.content || 'No messages'}
                      </div>
                      {conv.lastMessage && (
                        <div style={{ fontSize: 11, color: '#9CA3AF', marginTop: 2 }}>
                          {new Date(conv.lastMessage.created_at).toLocaleTimeString()}
                        </div>
                      )}
                    </td>
                    <td style={{ padding: 12, textAlign: 'center' }}>
                      {conv.messageCount}
                    </td>
                    <td style={{ padding: 12, fontSize: 13 }}>
                      {new Date(conv.updated_at).toLocaleDateString()}
                    </td>
                    <td style={{ padding: 12, textAlign: 'center' }}>
                      <span style={{
                        display: 'inline-block',
                        width: 10,
                        height: 10,
                        borderRadius: '50%',
                        backgroundColor: conv.is_flagged ? '#EF4444' : '#10B981',
                        marginRight: 6
                      }} />
                      {conv.is_flagged ? 'Yes' : 'No'}
                    </td>
                    <td style={{ padding: 12, textAlign: 'center' }}>
                      <button
                        onClick={() => toggleFlag(conv.id, conv.is_flagged)}
                        style={{
                          padding: '4px 12px',
                          backgroundColor: conv.is_flagged ? '#FEE2E2' : '#F3F4F6',
                          color: conv.is_flagged ? '#DC2626' : '#374151',
                          border: 'none',
                          borderRadius: 6,
                          fontSize: 12,
                          cursor: 'pointer',
                          marginRight: 8
                        }}
                      >
                        {conv.is_flagged ? 'Unflag' : 'Flag'}
                      </button>
                      <button
                        onClick={() => viewMessages(conv)}
                        style={{
                          padding: '4px 12px',
                          backgroundColor: '#6366F1',
                          color: 'white',
                          border: 'none',
                          borderRadius: 6,
                          fontSize: 12,
                          cursor: 'pointer'
                        }}
                      >
                        View
                      </button>
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>
      ) : (
        /* Messages View */
        <div>
          <div style={{ marginBottom: 16, display: 'flex', alignItems: 'center', gap: 16 }}>
            <button
              onClick={() => setShowMessages(false)}
              style={{
                padding: '8px 16px',
                backgroundColor: '#F3F4F6',
                border: '1px solid #E5E7EB',
                borderRadius: 8,
                cursor: 'pointer'
              }}
            >
              ← Back to conversations
            </button>
            <h2 style={{ fontSize: 20, fontWeight: 600 }}>Conversation with {selectedConversation?.participants}</h2>
            {selectedConversation?.is_flagged && (
              <span style={{
                padding: '4px 12px',
                backgroundColor: '#FEE2E2',
                color: '#DC2626',
                borderRadius: 20,
                fontSize: 12,
                fontWeight: 500
              }}>
                ⚠️ Flagged
              </span>
            )}
          </div>
          
          <div style={{
            backgroundColor: '#F9FAFB',
            borderRadius: 12,
            padding: 20,
            maxHeight: 600,
            overflowY: 'auto'
          }}>
            {messages.map(msg => (
              <div key={msg.id} style={{ marginBottom: 16 }}>
                <div style={{
                  display: 'flex',
                  alignItems: 'baseline',
                  gap: 8,
                  marginBottom: 4
                }}>
                  <span style={{ fontWeight: 600, fontSize: 13 }}>
                    {msg.sender?.full_name || 'System'}
                  </span>
                  <span style={{ fontSize: 11, color: '#9CA3AF' }}>
                    {new Date(msg.created_at).toLocaleString()}
                  </span>
                  {msg.sender?.role === 'admin' && (
                    <span style={{
                      fontSize: 10,
                      backgroundColor: '#6366F1',
                      color: 'white',
                      padding: '2px 6px',
                      borderRadius: 4
                    }}>
                      Admin
                    </span>
                  )}
                </div>
                <div style={{
                  backgroundColor: msg.type === 'system' ? '#F3F4F6' : 'white',
                  padding: '10px 14px',
                  borderRadius: 12,
                  border: '1px solid #E5E7EB',
                  fontStyle: msg.type === 'system' ? 'italic' : 'normal',
                  color: msg.type === 'system' ? '#6B7280' : '#111827'
                }}>
                  {msg.type === 'image' ? (
                    <img src={msg.media_url} alt="Message attachment" style={{ maxWidth: 300, maxHeight: 200, borderRadius: 8 }} />
                  ) : (
                    msg.content
                  )}
                </div>
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
};

export default AdminMessages;